import { type FC, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TriggerButton } from "widgets";

import { pluralizeWord } from "../../../app-utils/string.js";
import {
  type IEditCellChange,
  type IEditable,
  saveDataEditingChanges,
  setPhenotypeEditingGridChanges,
} from "../../../redux/modules/data-editing.js";
import { type ITableColumn } from "../../../redux/modules/table-data.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import NewPhenotypeTagDialog from "../../core/NewPhenotypeTagDialog.js";
import SelectPhenotypeTagButton from "../../core/SelectPhenotypeTagDialog.js";
import { ModalControlledOverlay, PopOverActionWidget } from "../../core/overlays/index.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import EditingGrid, { useCountInvalidCells } from "./EditingGrid.js";

type IPhenotypeEditingGridScreenOwnProps = IPlaybookWizardStepProvidedProps;

/**
 * Screen with EditTable for modification of Phenotype data
 */
const PhenotypeEditingGridScreen: FC<IPhenotypeEditingGridScreenOwnProps> = (
  props: IPhenotypeEditingGridScreenOwnProps & IPlaybookWizardStepInjectedProps,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const columns = useSelector<IAppState, ITableColumn[]>((state) => state.dataEditing.columns);
  const rowData = useSelector<IAppState, IEditable[]>((state) => state.dataEditing.rowData);
  const isLoading = useSelector<IAppState, boolean>((state) => state.dataEditing.isLoading);

  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [propertyTypeToEdit, setPropertyTypeToEdit] = useState<number>(null);

  const proceedWithNextStep = () => {
    setErrorMessage(null);
    setSubmitting(true);
    dispatch(saveDataEditingChanges())
      .then(() => setSubmitting(false))
      .then(props.proceedWithNextStep)
      .catch((error: Error) => {
        setErrorMessage(error.message);
        setSubmitting(false);
      });
  };

  const setEditingGridChanges = useCallback(
    (changes: IEditCellChange[]) => dispatch(setPhenotypeEditingGridChanges(changes)),
    [],
  );

  const invalidCellsCount: number = useCountInvalidCells(rowData);

  return (
    <PlaybookWizardStep
      {...props}
      tools={
        <div className="playbook--tools">
          <SelectPhenotypeTagButton />
          <PopOverActionWidget
            title={"Create a new phenotype tag, visible as a new phenotype column"}
            actionWidget={({ open }) => (
              <TriggerButton icon={"add"} onPress={() => open()} label={"New Phenotype"} showLabel />
            )}
          >
            {({ close }) => <NewPhenotypeTagDialog close={close} />}
          </PopOverActionWidget>
        </div>
      }
      wizardFooterData={{
        error:
          invalidCellsCount > 0
            ? `${invalidCellsCount} ${pluralizeWord("cell", invalidCellsCount)} ${pluralizeWord(
                "is",
                invalidCellsCount,
              )} invalid.`
            : errorMessage,
        nextButtonProps: {
          label: "Save Assignments",
          isDisabled: invalidCellsCount > 0 || rowData.length === 0,
          isLoading: submitting,
          onPress: proceedWithNextStep,
        },
      }}
    >
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <EditingGrid
          columns={columns}
          rowData={rowData}
          isLoading={isLoading}
          setEditingGridChanges={setEditingGridChanges}
          tableProps={{
            headerComponentParams: {
              onClickEditButtonCallback: (columnId) => setPropertyTypeToEdit(columnId), // ugly workaround to get hold of the organism property type ID
            },
          }}
        />
      </div>
      {propertyTypeToEdit && (
        <ModalControlledOverlay
          isOpen={true}
          close={() => {
            setPropertyTypeToEdit(null);
          }}
        >
          {({ close }) => <NewPhenotypeTagDialog propertyTypeIdToEdit={propertyTypeToEdit} close={close} />}
        </ModalControlledOverlay>
      )}
    </PlaybookWizardStep>
  );
};

export { PhenotypeEditingGridScreen };
export default PhenotypeEditingGridScreen;
